import { useState, useEffect, useRef, useCallback } from "react";

import "./Hero01.css";
import RivePlayer from "../../extras/RivePlayer01";
import Button01 from "../../core/Button01";

import brand01 from "../../../img/brands/chaturbate.svg";
import brand02 from "../../../img/brands/reddit.svg";
import brand03 from "../../../img/brands/tiktok.svg";
import brand04 from "../../../img/brands/manyvids.svg";
import brand05 from "../../../img/brands/youtube.svg";
import brand06 from "../../../img/brands/onlyfans.svg";
import brand07 from "../../../img/brands/instagram.svg";
import logo from "../../../img/logo/favicon.png";

const PRELOADER_MIN_MS = 2000;
const PRELOADER_MAX_MS = 5000;

function Hero01() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showPreloader, setShowPreloader] = useState(true);

  const sessionStartRef = useRef(0);
  const dismissedRef = useRef(false);
  const minTimeoutRef = useRef(null);
  const maxTimeoutRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handleChange = (e) => setIsMobile(e.matches);
    handleChange(mq);
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);

  const heroSrc = isMobile ? "/rive/hero_mobile.riv" : "/rive/hero_desktop.riv";

  const dismissPreloader = useCallback(() => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    if (minTimeoutRef.current != null) {
      clearTimeout(minTimeoutRef.current);
      minTimeoutRef.current = null;
    }
    if (maxTimeoutRef.current != null) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = null;
    }
    setShowPreloader(false);
  }, []);

  useEffect(() => {
    dismissedRef.current = false;
    setShowPreloader(true);
    sessionStartRef.current = performance.now();

    if (minTimeoutRef.current != null) {
      clearTimeout(minTimeoutRef.current);
      minTimeoutRef.current = null;
    }
    if (maxTimeoutRef.current != null) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = null;
    }

    maxTimeoutRef.current = setTimeout(dismissPreloader, PRELOADER_MAX_MS);

    return () => {
      if (minTimeoutRef.current != null) clearTimeout(minTimeoutRef.current);
      if (maxTimeoutRef.current != null) clearTimeout(maxTimeoutRef.current);
    };
  }, [heroSrc, dismissPreloader]);

  const handleRiveReady = useCallback(() => {
    if (dismissedRef.current) return;
    const elapsed = performance.now() - sessionStartRef.current;
    const remaining = Math.max(0, PRELOADER_MIN_MS - elapsed);
    if (remaining === 0) {
      dismissPreloader();
    } else {
      if (minTimeoutRef.current != null) clearTimeout(minTimeoutRef.current);
      minTimeoutRef.current = setTimeout(dismissPreloader, remaining);
    }
  }, [dismissPreloader]);

  useEffect(() => {
    if (!showPreloader) return;

    const html = document.documentElement;
    const body = document.body;

    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;

    html.classList.add("app--heroPreloading");
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    return () => {
      html.classList.remove("app--heroPreloading");
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, [showPreloader]);

  return (
    <div className="Hero01 section" id="Hero01" aria-busy={showPreloader}>
      <div
        className={`Hero01-preloader ${showPreloader ? "" : "Hero01-preloader--done"}`}
        aria-hidden={!showPreloader}
      >
        <div className="Hero01-preloader-inner">
          <div className="Hero01-preloader-logo">
            <div className="Hero01-preloader-spinner" aria-hidden />
            <img src={logo} alt="SERVED logo" />
          </div>

          <p className="Hero01-preloader-text">Loading…</p>
        </div>
      </div>

      <div className="Hero01-box">
        <div className="Hero01-left">
          <div className="Hero01-text">
            <h1>
              Private DMCA Enforcement & Verified Creator{" "}
              <br className="Hero01-text-break" />
              Network
            </h1>

            <p>
              Invite-only DMCA protection network built for elite creators
              protecting their digital empires.
            </p>

            <Button01 />
          </div>

          <div className="Hero01-brands-box">
            <p>YOU'VE SEEN US ON:</p>

            <div className="Hero01-brands-carousel">
              <div className="Hero01-brands">
                <img src={brand01} alt="Brand 01" />
                <img src={brand02} alt="Brand 02" />
                <img src={brand03} alt="Brand 03" />
                <img src={brand04} alt="Brand 04" />
                <img src={brand05} alt="Brand 05" />
                <img src={brand06} alt="Brand 06" />
                <img src={brand07} alt="Brand 07" />
              </div>
              <div className="Hero01-brands">
                <img src={brand01} alt="Brand 01" />
                <img src={brand02} alt="Brand 02" />
                <img src={brand03} alt="Brand 03" />
                <img src={brand04} alt="Brand 04" />
                <img src={brand05} alt="Brand 05" />
                <img src={brand06} alt="Brand 06" />
                <img src={brand07} alt="Brand 07" />
              </div>
            </div>
          </div>
        </div>

        <RivePlayer src={heroSrc} key={heroSrc} onRiveReady={handleRiveReady} />
      </div>

      <div className="Hero01-bg" />
    </div>
  );
}

export default Hero01;
